/*
 * Copyright (C) 2014 Spanning Cloud Apps LLC.  All rights reserved.
 */

/*
 * This module provides access to the bulk restore functions of the server.
 */
angular
  .module('services.bulkrestore', [
    'http.bulkrestore',
    'http.metadataRestore',
    'http.queue',
    'http.sobject',
    'sbsf.constants',
    'services.bulkrestoredata',
    'services.progress'
  ])
  .factory('BulkRestoreService', [
    '$q',
    '$filter',
    '$rootScope',
    'httpBulkRestore',
    'httpMetadataRestore',
    'httpQueue',
    'httpSObject',
    'progressFactory',
    'SOBJECT_STATE',
    'RESTORE_OVERWRITE_STRATEGIES',
    function (
      $q,
      $filter,
      $rootScope,
      httpBulkRestore,
      httpMetadataRestore,
      httpQueue,
      httpSObject,
      progressFactory,
      SOBJECT_STATE,
      RESTORE_OVERWRITE_STRATEGIES
    ) {
      /**
       * Cache of the total number of metadata items for each component type by batch.
       *
       * @type {object}
       */
      var metadataComponentTypeTotals = {};

      /**
       * Cache of metadata items by batch and component type
       *
       * @type {object}
       */
      var metadataByBatchAndComponentType = {};

      /**
       * Cache of object types
       *
       * @type {string[]}
       */
      var objectTypes;

      /**
       * Cache of field definitions by object type.
       *
       * @type {object}
       */
      var objectFieldsByType = {};

      /**
       * Cache of overwrite value presets
       *
       * @type {object[]}
       */
      var overwriteValuePresets;

      /**
       * Cache of object types in a verified CSV, by batch id
       */
      var csvObjectTypesByBatch = {};

      const i18n = $filter('i18n');

      // Expose the functions only, not the data
      return {
        convertIsDeleted: convertIsDeleted,
        deleteOverwriteValuePreset: deleteOverwriteValuePreset,
        downloadInvalidIdCsv: downloadInvalidIdCsv,
        getBatchDetails: getBatchDetails,
        getCsvObjectTypes: getCsvObjectTypes,
        getCsvVerificationResults: getCsvVerificationResults,
        getFieldsForObjectTypes,
        getMetadataComponentTypeTotals: getMetadataComponentTypeTotals,
        getMetadataForBatchAndComponentType: getMetadataForBatchAndComponentType,
        getObjectRecordDiff: getObjectRecordDiff,
        getObjectRecordPreview: getObjectRecordPreview,
        getObjectRecordPreviewChildren: getObjectRecordPreviewChildren,
        getObjectTypes: getObjectTypes,
        getOverwriteValuePresets: getOverwriteValuePresets,
        getPresetFieldsFromOverwriteValues: getPresetFieldsFromOverwriteValues,
        getPreSignedCsvDownloadUrl: getPreSignedCsvDownloadUrl,
        getPreSignedPostUrl: getPreSignedPostUrl,
        getRecordStateText,
        searchRecordsPage,
        getSelectedObjectTypesWithRestorableFields: getSelectedObjectTypesWithRestorableFields,
        getSelectedObjectTypesWithUpdateableFields: getSelectedObjectTypesWithUpdateableFields,
        saveOverwriteValuePreset: saveOverwriteValuePreset,
        submitCsvVerification: submitCsvVerification,
        submitExport: submitExport,
        submitRestore: submitRestore
      };

      /**
       * Converts the 'isDeleted' value in an sobject record into a boolean. As it comes from the server, isDeleted is
       * a bitset where bit 0 indicates isDeleted or isUpdated and bit 1 indicates isUndeleted.
       *
       *  The four possible values are:
       *   0b00 Updated
       *   0b01 Deleted
       *   0b10 Undeleted Updated
       *   0b11 Undeleted Deleted
       *
       * Based on the property name, the temptation is to interpret this value as a boolean, so let's make it one.
       *
       * @param {object} record - the record to be converted.
       */
      function convertIsDeleted(record) {
        // Make sure there's a value to test
        record.isDeleted = record.isDeleted || 0;

        record.isUndeleted = (record.isDeleted & 0b10) === 0b10;
        record.isDeleted = (record.isDeleted & 0b01) === 0b01;
        record.isUpdated = !record.isDeleted;
      }

      /**
       * Deletes a named overwrite value preset.
       *
       * @param {string} presetName - The name of the preset to delete.
       * @returns {Promise} A promise that resolves to the new list of presets.
       */
      function deleteOverwriteValuePreset(presetName) {
        var foundPreset = _.findWhere(overwriteValuePresets, { name: presetName });

        if (!foundPreset) {
          throw new Error('No preset found with name ' + presetName);
        }

        // Use the secondary loading mask because the regular loading mask is turned on and off in
        // getOverwriteValuePresets.
        $rootScope.showSecondaryLoadingMask = true;

        return httpBulkRestore
          .deletePreset(foundPreset.id)
          .then(function () {
            // Pull the updated list into the cache
            var refresh = true;
            return getOverwriteValuePresets(refresh);
          })
          .finally(function () {
            $rootScope.showSecondaryLoadingMask = false;
          });
      }

      /**
       * Generates and downloads a CSV of invalid ids from the CSV verification table.
       *
       * @param {Number} batchId - The batch id for the CSV verification.
       * @returns {Promise} - A promise that resolves to an object.
       */
      function downloadInvalidIdCsv(batchId) {
        return httpBulkRestore.getInvalidIdCsv({ batchId: batchId });
      }

      /**
       * Returns the bulk restore details for a batch.
       *
       * @param {Object} params - A parameters object
       * @param {Number} params.batchId - The batch id to search against.
       * @param {Number} [params.page] - The current selected page.
       * @param {Number} [params.pageSize] - The number of records per page.
       * @param {String} [params.filter] -  The filter string to apply.
       * @param {String} [params.sort='sfName'] -  Property to sort by.
       * @param {Boolean} [params.ascending=true] - True to sort in ascending order.
       * @returns {Promise} - A promise that when resolved will include an object.
       */
      function getBatchDetails(params) {
        return httpBulkRestore.getDetails(params);
      }

      /**
       * Returns the list of object types in a CSV verification batch.
       *
       * @param {Number} batchId - The batch id for the CSV verification.
       * @param {Boolean} [refresh] - if true, we'll send a server request even if data is cached locally.
       * @returns {Promise<String[]>} - A promise that resolves to the list of object types.
       */
      function getCsvObjectTypes(batchId, refresh) {
        var types = csvObjectTypesByBatch[batchId];
        if (types && !refresh) {
          return $q.when(types);
        }

        return httpBulkRestore.getCsvObjectTypes(batchId).then(function (types) {
          csvObjectTypesByBatch[batchId] = types;
          return types;
        });
      }

      /**
       * Returns a page of object records from the most recent CSV verification.
       *
       * @param {Object} bulkRestoreData - the configured settings for the bulk restore.
       * @param {Object} pagingParams - A parameters object for paging
       * @param {Number} [pagingParams.page] - The current selected page.
       * @param {Number} [pagingParams.pageSize] - The number of records per page.
       * @param {String} [pagingParams.sort='lastModified'] -  Property to sort by.
       * @param {Boolean} [pagingParams.ascending=true] - True to sort in ascending order.
       *
       * @returns {Promise<{numberOfRecords: number, records: object[]}>} A promise that resolves to the total number of
       *   records in the CSV verification and the list of records on the specified page.
       */
      function getCsvVerificationResults(bulkRestoreData, pagingParams) {
        var types = bulkRestoreData.getCsvObjectTypes();
        var params = _.extend(
          {
            type: types.length === 1 ? types[0] : undefined,
            batchId: bulkRestoreData.getCsvBatchId()
          },
          pagingParams
        );

        return httpBulkRestore.getVerifiedRecordsByBatchId(params);
      }

      /**
       * Returns the object fields definitions for a Salesforce object type.
       *
       * @param {String[]} objectTypes - The object types of the fields to retrieve.
       * @param {Boolean} [refresh] - if true, we'll send a server request even if data is cached locally.
       * @returns {Promise} A promise that resolves to a list of fields.
       */
      function getFieldsForObjectTypes(objectTypes, refresh) {
        if (!refresh) {
          let fields = {};
          for (const objectType of objectTypes) {
            if (objectFieldsByType[objectType]) {
              fields[objectType] = objectFieldsByType[objectType];
            }
          }
          if (Object.keys(fields).length === objectTypes.length) {
            return $q.when(fields);
          }
        }

        $rootScope.showLoadingMask = true;

        return httpBulkRestore
          .getFieldsForObjects(objectTypes)
          .then(function (result) {
            for (const key of Object.keys(result)) {
              objectFieldsByType[key] = result[key];
            }
            return result;
          })
          .finally(function () {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Gets the total number of backed up metadata items by component type.
       *
       * @param {Number} batchId - The batch id of the metadata to list.
       * @param {Boolean} [refresh] - flag to force a server request even if data is cached locally.
       * @returns {Promise} - A promise that resolves to an array of metadata backup details, one per component type.
       */
      function getMetadataComponentTypeTotals(batchId, refresh) {
        // Check the cache unless we've been asked to refresh
        if (!refresh && _.has(metadataComponentTypeTotals, batchId)) {
          return $q.when(metadataComponentTypeTotals[batchId]);
        }

        $rootScope.showLoadingMask = true;

        return httpMetadataRestore
          .getComponentTypeTotals(batchId)
          .then(function (results) {
            metadataComponentTypeTotals[batchId] = results;
            return results;
          })
          .finally(function () {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Get the list of metadata items for a batch and component type.
       *
       * @param {Number} batchId - The batch id of the metadata to list.
       * @param {String} componentType - The type of component whose items to list.
       * @param {Boolean} [refresh] - flag to force a server request even if data is cached locally.
       * @returns {Promise} - A promise that resolves to an array of SfMetadataInfo objects for the specified component
       *   type.
       */
      function getMetadataForBatchAndComponentType(batchId, componentType, refresh) {
        // Check the cache unless we've been asked to refresh
        // We'll cache by batch and component type
        var key = batchId + '.' + componentType;
        if (!refresh && _.has(metadataByBatchAndComponentType, key)) {
          return $q.when(metadataByBatchAndComponentType[key]);
        }

        $rootScope.showLoadingMask = true;

        return httpMetadataRestore
          .getItemsForBatchAndComponentType(batchId, componentType)
          .then(function (items) {
            metadataByBatchAndComponentType[key] = items;
            return items;
          })
          .finally(function () {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Gets the diff details for an object record.
       *
       * @param {string} id - The Salesforce id for the record.
       * @returns {Promise} - A promise that resolves into the diff details.
       */
      function getObjectRecordDiff(id, batchId) {
        return httpSObject.getDiff(id, batchId);
      }

      /**
       * Gets the preview details for an object record.
       *
       * @param {number} id - The Spanning id for the record.
       * @param {number} batchId - The batch id for the record.
       * @returns {Promise} - A promise that resolves into details of record.
       */
      function getObjectRecordPreview(id, batchId) {
        return httpBulkRestore.getRecordPreview(id, batchId);
      }

      /**
       * Gets the children of an object record being previewed.
       *
       * @param {number} id - The Spanning id for the record.
       * @param {number} batchId - The batch id for the record.
       * @returns {Promise} - A promise that resolves into children of record.
       */
      function getObjectRecordPreviewChildren(id, batchId) {
        return httpBulkRestore.getRecordChildren(id, batchId);
      }

      /**
       * Get the object types to restore. Caches results in memory.
       *
       * @param {Boolean} [refresh] - Flag to force the retrieval for types from server.
       * @returns {Promise} - A promise that resolves into a bulk restore types object.
       */
      function getObjectTypes(refresh, params) {
        // Check the cache unless we've been asked to refresh
        if (!refresh && objectTypes) {
          return $q.when(objectTypes);
        }

        $rootScope.showLoadingMask = true;

        return httpBulkRestore
          .getTypes(params)
          .then(function (types) {
            objectTypes = types;
            return objectTypes;
          })
          .finally(function () {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Returns the pre-defined sets of overwrite values.
       *
       * @param {Boolean} [refresh] - Flag to force the retrieval from server.
       * @returns {Promise} A promise that will resolve to the presets.
       */
      function getOverwriteValuePresets(refresh) {
        // Check the cache unless we've been asked to refresh
        if (!refresh && overwriteValuePresets) {
          return $q.when(overwriteValuePresets);
        }

        $rootScope.showLoadingMask = true;

        return httpBulkRestore
          .getPresets()
          .then(function (presets) {
            overwriteValuePresets = presets;
            return overwriteValuePresets;
          })
          .finally(function () {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Returns the preset fields for the supplied set of overwrite values. The overwrite values have extra, UI-only
       * data that we don't need to send to the server when creating or updating a preset.
       *
       * @param {object[]} ovEntries - The overwrite value entries making up the preset.
       * @returns {object[]} - The list of preset fields representing the specified overwrite values.
       */
      function getPresetFieldsFromOverwriteValues(ovEntries) {
        // This list should correspond to the list in services/src/dto/OverwriteField.ts
        // We intentionally leave out 'value' because we're going to conditionally copy it, depending on the strategy
        var presetFieldProps = ['id', 'orgId', 'templateId', 'objectType', 'objectField', 'strategy'];

        // Convert each overwrite value into a preset field
        return _.map(ovEntries, function (ovEntry) {
          // Copy only the necessary properties
          var presetField = _.pick(ovEntry, presetFieldProps);

          // value is used only with the CONSTANT strategy
          if (ovEntry.strategy === RESTORE_OVERWRITE_STRATEGIES.CONSTANT) {
            presetField.value = ovEntry.value;
          }

          return presetField;
        });
      }

      /**
       * Returns a pre-signed url for downloading a CSV file from s3.
       *
       * @param {Number} batchId - The id of the bulk restore batch whose CSV is to be downloaded.
       * @returns {Promise<String>} - A promise that resolves to the pre-signed URL.
       */
      function getPreSignedCsvDownloadUrl(batchId) {
        return httpBulkRestore.getPreSignedCsvDownloadUrl(batchId);
      }

      /**
       * Returns a pre-signed URL for uploading a file to S3.
       *
       * @param {Number} timestamp - The timestamp to use when signing.
       * @returns {Promise<String>} - A promise that resolves to the URL.
       */
      function getPreSignedPostUrl(timestamp) {
        return httpBulkRestore.getPreSignedPostUrl(timestamp);
      }

      /**
       * Search a page of object records
       *
       * @param {Object} bulkRestoreData - the configured settings for the bulk restore.
       * @param {number} pageSize
       *
       * @returns {Promise<{numberOfRecords: number, records: object[]}>} A promise that resolves to the total number of
       *   records in the search results and the list of records on the specified page.
       */
      function searchRecordsPage(bulkRestoreData, pageSize) {
        const lastSearch = bulkRestoreData.getLastSearchParams();
        const pageToken = bulkRestoreData.getSearchPageToken();

        const browseMode = bulkRestoreData.isManualObjectBrowse();
        const startBatchId = lastSearch ? lastSearch.startBatchId : bulkRestoreData.getSearchStartBatchId();

        const params = {
          sObjectType: lastSearch ? lastSearch.objectType : bulkRestoreData.getSearchObjectType(),
          pageToken,
          searchToken: bulkRestoreData.getSearchToken(),
          pageBatchId: bulkRestoreData.getSearchPageBatchId(),
          filter: lastSearch ? lastSearch.searchText : bulkRestoreData.getSearchText(),
          startSystemModstamp: lastSearch ? lastSearch.startSystemModstamp : bulkRestoreData.getStartSystemModstamp(),
          endSystemModstamp: lastSearch ? lastSearch.endSystemModstamp : bulkRestoreData.getEndSystemModstamp(),
          sortBy: bulkRestoreData.getSortBy(),
          ascending: bulkRestoreData.getAscending(),
          pageSize
        };

        params.searchMode = browseMode ? 'BROWSE' : 'INTERVAL';

        if (browseMode) {
          params.browseBatchId = startBatchId;
        } else {
          params.sObjectStates = lastSearch ? lastSearch.objectStates : bulkRestoreData.getSearchObjectStates();
          params.batchId = bulkRestoreData.getBackupBatchId();
          params.startBatchId = startBatchId;
          params.endBatchId = lastSearch ? lastSearch.endBatchId : bulkRestoreData.getSearchEndBatchId();
        }

        return httpBulkRestore.searchRecordsPage(params).then((results) => {
          // Post-process the results to convert the isDeleted values into true booleans.
          _.each(results.records, convertIsDeleted);
          return results;
        });
      }

      /**
       * Returns the list of object types of the currently selected records. Only those object types with at least one
       * creatable or updateable field are returned.
       *
       * @param {Object} bulkRestoreData - the configured settings for the bulk restore.
       * @returns {Promise} a promise that resolves with the list.
       */
      function getSelectedObjectTypesWithRestorableFields(bulkRestoreData) {
        var selectedTypes = bulkRestoreData.getSelectedObjectTypesList();
        var finalTypes = [];

        var promises = [];
        _.each(selectedTypes, function (type) {
          // Check for restorable fields
          const promise = getFieldsForObjectTypes([type]).then(function (result) {
            const fields = result[type];
            // Return it if it has at least one restorable field. This is filtered for creatable or updateable in the
            // server, so we only need to check the length.
            if (fields.length > 0) {
              finalTypes.push(type);
            }
          });
          promises.push(promise);
        });

        // Wait for all field checking to complete
        return $q.all(promises).then(function () {
          return finalTypes;
        });
      }

      /**
       * From the currently selected records, returns the list of object types with at least one updateable field.
       *
       * @param {Object} bulkRestoreData - the configured settings for the bulk restore.
       * @returns {Promise} a promise that resolves with the list.
       */
      function getSelectedObjectTypesWithUpdateableFields(bulkRestoreData) {
        var selectedTypes = bulkRestoreData.getSelectedObjectTypesList();
        var finalTypes = [];

        var promises = [];
        _.each(selectedTypes, function (type) {
          // Check for updateable fields
          const promise = getFieldsForObjectTypes([type]).then(function (result) {
            const fields = result[type];
            // Return it if it has at least one updateable field. The server returns both creatable and updateable.
            var counts = _.countBy(fields, function (field) {
              return field.updateable ? 'updateable' : 'readonly';
            });

            if (counts.updateable > 0) {
              finalTypes.push(type);
            }
          });
          promises.push(promise);
        });

        // Wait for all field checking to complete
        return $q.all(promises).then(function () {
          return finalTypes;
        });
      }

      /**
       * Saves an overwrite value preset. If the name corresponds to an existing preset, that preset will be updated. If
       * not, a new preset will be saved under that name.
       *
       * @param {string} presetName - The name of the preset to save.
       * @param {object[]} ovEntries - The overwrite value entries making up the preset.
       * @returns {Promise} A promise that resolves to the new list of presets.
       */
      function saveOverwriteValuePreset(presetName, ovEntries) {
        var presetToSave = _.findWhere(overwriteValuePresets, { name: presetName });
        var presetFields = getPresetFieldsFromOverwriteValues(ovEntries);

        if (presetToSave) {
          presetToSave.fields = presetFields;
        } else {
          // We're going to create new fields, so get rid of any previous ids
          _.each(presetFields, function (field) {
            field.id = null;
          });

          presetToSave = {
            id: null,
            fields: presetFields
          };
        }

        presetToSave.name = presetName;

        // Use the secondary loading mask because the regular loading mask is turned on and off in
        // getOverwriteValuePresets.
        $rootScope.showSecondaryLoadingMask = true;

        return httpBulkRestore
          .savePreset(presetToSave)
          .then(function () {
            // Pull the updated list into the cache
            var refresh = true;
            return getOverwriteValuePresets(refresh);
          })
          .finally(function () {
            $rootScope.showSecondaryLoadingMask = false;
          });
      }

      /**
       * Submits a bulk restore CSV verification to be queued.
       *
       * @params {object} bulkRestoreData - the configured settings for the bulk restore.
       * @params {number} timestamp - the timestamp used when the file was uploaded.
       * @params {string} parentJobType - Parent job type for which we verify CSV
       * @returns {Promise} A promise that resolves to the queue result if the CSV verification was successful.
       */
      function submitCsvVerification(bulkRestoreData, timestamp, parentJobType) {
        var params = {
          batchId: bulkRestoreData.getBackupBatchId(),
          uploadTimestamp: timestamp,
          fileName: bulkRestoreData.getUploadFileName(),
          sourceOrgId: bulkRestoreData.getSourceOrgId(),
          targetOrgId: bulkRestoreData.getDestOrgId(),
          accessTokenOwnerId: bulkRestoreData.getDestOrgAccessTokenOwnerId(),
          parentJobType
        };

        return httpQueue.submitBulkRestoreCsvVerification(params).then(function (res) {
          if (res.status === 0) {
            return res;
          } else {
            return $q.reject(res);
          }
        });
      }

      /**
       * Submits a bulk restore preview (Export) to be queued.
       *
       * @params {object} bulkRestoreData - the configured settings for the bulk restore.
       * @returns {Promise} A promise that resolves when the export is queued.
       */
      function submitExport(bulkRestoreData) {
        var params = bulkRestoreData.getParamsForExport();
        return httpQueue.submitBulkRestorePreview(params);
      }

      /**
       * Submits a bulk restore to be queued.
       *
       * @params {object} bulkRestoreData - the configured settings for the bulk restore.
       * @returns {Promise} A promise that resolves when the export is queued.
       */
      function submitRestore(bulkRestoreData) {
        var params = bulkRestoreData.getParamsForRestore();
        return httpQueue.submitBulkRestore(params);
      }

      /**
       * Get the text of status of record
       * @param { Object } record
       * @param { Object } bulkRestoreData
       */
      function getRecordStateText(record, bulkRestoreData) {
        const lastSearchParams = bulkRestoreData.getLastSearchParams();
        let searchObjectStates = lastSearchParams
          ? lastSearchParams.objectStates
          : bulkRestoreData.getSearchObjectStates();
        if (record && record.isDeleted) {
          return i18n('DELETED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.UPDATED) >= 0) {
          return i18n('UPDATED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.NEW) >= 0) {
          return i18n('NEW_OR_UPDATED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.UNCHANGED) >= 0) {
          return i18n('UNCHANGED');
        }
        if (searchObjectStates.indexOf(SOBJECT_STATE.DELETED) >= 0) {
          return i18n('DELETED');
        }
        return i18n('UPDATED');
      }
    }
  ]);
